import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import { z } from 'zod'
import { CognitoAuthService } from '../services/cognitoAuth'
import clientConverge from '../assets/presidio_logo.png'
import './LoginPage.css'

const forgotPasswordSchema = z.object({
  username: z.string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, 'Username or email is required')
    .refine((val) => val.length >= 3, 'Username must be at least 3 characters'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

const ForgotPasswordPage = () => {
  const navigate = useNavigate()

  // State for error handling and loading
  const [resetError, setResetError] = React.useState<string>('')
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [isCodeSent, setIsCodeSent] = React.useState<boolean>(false)
  const [submittedUsername, setSubmittedUsername] = React.useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      // Clear any previous errors and start loading
      setResetError('')
      setIsSubmitting(true)

      const trimmedUsername = data.username.trim()

      if (!trimmedUsername) {
        setResetError('Username cannot be empty or just whitespace')
        setIsSubmitting(false)
        return
      }

      await CognitoAuthService.requestPasswordReset(trimmedUsername)

      // Success - show confirmation
      setIsCodeSent(true)
      setSubmittedUsername(trimmedUsername)
      setIsSubmitting(false)

    } catch (error: any) {
      console.error('Forgot password error:', error)

      // Provide more specific error messages
      let errorMessage = 'Failed to send password reset code. Please try again.'

      // Handle both legacy __type and modern name properties
      const errorType = error.__type || error.name || error.code
      console.error('Detected error type:', errorType)

      switch (errorType) {
        case 'UserNotFoundException':
        case 'UserNotFound':
          errorMessage = 'Username not found. Please check your username and try again.'
          break
        case 'InvalidParameterException':
        case 'InvalidParameter':
          errorMessage = 'Invalid username format. Please check your input and try again.'
          break
        case 'LimitExceededException':
        case 'TooManyRequestsException':
          errorMessage = 'Too many requests. Please wait a few minutes before trying again.'
          break
        case 'NotAuthorizedException':
          errorMessage = 'Password reset is not allowed for this account. Please contact support.'
          break
        case 'InternalErrorException':
        case 'InternalError':
          errorMessage = 'An internal error occurred. Please try again later.'
          break
        default:
          if (error.message) {
            errorMessage = error.message
          }
          break
      }

      setResetError(errorMessage)
      setIsSubmitting(false)
    }
  }

  const handleContinueToReset = () => {
    // Navigate to reset password page with username
    navigate('/reset-password', {
      state: { username: submittedUsername },
      replace: true
    })
  }

  if (isCodeSent) {
    return (
      <div className="login-background flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
          {/* Header with Logo and Branding */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shield-icon-container">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="lock-icon">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <div className="mx-auto mb-4 flex flex-col items-center justify-center">
              <p className="data-trust-accelerator-title">Data Trust Accelerator</p>
            </div>
            <h2 className="text-2xl font-semibold mb-3 text-gray-900">
              Check Your Email
            </h2>
            <p className="text-gray-500 text-sm">We've sent a password reset code to your email</p>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg shadow-sm mb-6">
              <div className="flex items-start justify-center">
                <div className="text-center">
                  <p className="text-sm text-green-700 font-medium mb-2">
                    Password reset code sent successfully!
                  </p>
                  <p className="text-xs text-green-600">
                    Please check your email for the verification code and follow the instructions to reset your password.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleContinueToReset}
              className="w-full py-3 text-white font-medium rounded-lg text-sm transition-all duration-200 sign-in-button hover:shadow-lg hover:opacity-90 mb-4"
            >
              Continue to Reset Password
            </button>

            <div className="text-center">
              <Link to="/login" className="text-sm text-teal-500 hover:text-teal-600">
                Back to Sign In
              </Link>
            </div>
          </div>

          {/* Presidio Logo at bottom */}
          <div className="flex justify-center mt-6 pt-4 border-gray-100">
            <img src={clientConverge} alt="PRESIDIO" className="presidio-logo-image" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login-background flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
        {/* Header with Logo and Branding */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shield-icon-container">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="lock-icon">
              <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
            </svg>
          </div>
          <div className="mx-auto mb-4 flex flex-col items-center justify-center">
            <p className="data-trust-accelerator-title">Data Trust Accelerator</p>
          </div>
          <h2 className="text-2xl font-semibold mb-3 text-gray-900">
            Forgot Password
          </h2>
          <p className="text-gray-500 text-sm">Enter your username to reset your password</p>
        </div>

        {/* Forgot Password Form */}
        <div className="login-form">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Validation Summary */}
            {errors.username && (
              <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                <div className="flex items-start">
                  <span className="text-yellow-500 text-sm mr-2">ℹ</span>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Please fix the following:</h4>
                    <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-1">
                      <li>Username: {errors.username.message}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                Username or Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  {...register('username')}
                  type="text"
                  autoComplete="username"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    errors.username
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter your username or email"
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Reset Error Message */}
            {resetError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <div>
                    <p className="text-sm text-red-700 font-medium">
                      {resetError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 text-white font-medium rounded-lg text-sm transition-all duration-200 sign-in-button ${
                isSubmitting
                  ? 'cursor-not-allowed opacity-75'
                  : 'hover:shadow-lg hover:opacity-90'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending Reset Code...
                </div>
              ) : (
                'Send Reset Code'
              )}
            </button>

            <div className="text-center mt-6">
              <Link to="/login" className="text-sm text-teal-500 hover:text-teal-600">
                Back to Sign In
              </Link>
            </div>
          </form>
        </div>

        {/* Presidio Logo at bottom */}
        <div className="flex justify-center mt-6 pt-4  border-gray-100">
          <img src={clientConverge} alt="PRESIDIO" className="presidio-logo-image" />
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
